import axios from 'axios';
import tools from "@/composables/tools";
import { Sesion, AlquilerCreateResponse } from '@/interfaces/Sesion'
import { Asistencia } from '@/interfaces/Asistencia'
import { Video } from '@/interfaces/Video'
import { Fotograma } from '@/interfaces/Fotograma'
import { SesionLog } from '@/interfaces/SesionLog'
import { useSesionStore } from '@/stores/sesionStore';
import { ContajeSesion } from '@/interfaces/ContajeSesion'

export interface RandomContajeMedia {
    tipo: 'video' | 'fotograma'
    id: number
    url: string
    thumb_url?: string | null
}

export interface RandomContajePayload {
    sesion_id: number
    medias: RandomContajeMedia[]
    acumulado?: number
}

/** Canal por el que el API envió el justificante de pago al alumno. */
export type CanalJustificante = 'email' | 'sms' | 'whatsapp'

export interface NotificacionCobro {
    canal: CanalJustificante | 'ninguno' | 'error'
    destino: string | null
    destino_ofuscado: string | null
    puede_email?: boolean
    puede_sms?: boolean
    puede_whatsapp?: boolean
    email_ficha?: string | null
    telefono_ofuscado?: string | null
    mensaje?: string | null
}

export interface ResultadoCobro {
    ok: boolean
    cart_id: number | null
    notificacion: NotificacionCobro | null
    message?: string
}

export type ConfirmacionJustificante = 'recibido' | 'no_recibido' | 'no_confirma'

export abstract class SesionService {
    static async get(sesion_id: number): Promise<Sesion> {
        try {
            const response = await axios.get('galidancia/sesion/' + sesion_id)
            //asignamos al store lo que traemos por api
            console.log('Asignamos al store la sesion que hemos traido por api', response.data)
            response.data.lastSync = new Date()
            useSesionStore().update(sesion_id, response.data);
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar la información de la sesión', 'danger', 5000)
            return {} as Sesion
        }
    }

    static async getAlquileres(fresh = false): Promise<Sesion[]> {
        try {
            const response = await axios.get('alquileres', {
                params: fresh ? { t: Date.now() } : undefined,
                headers: fresh ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } : undefined,
            })
            return response.data
        } catch (error: any) {
            if (error?.response?.status !== 429) {
                tools().toast('No se pudo cargar los alquileres', 'danger', 5000)
            }
            return [] as Sesion[]
        }
    }

    static async getSesiones(from: string, to: string): Promise<Sesion[]> {
        try {
            console.log('Solititando sesiones')
            const response = await axios.get('galidancia/sesions/' + from + '/' + to)
            response.data.map((sesion: Sesion) => {
                sesion.id = Number(sesion.id); //forzamos a que sea un número
                return sesion;
            });
            console.log('Sesiones recibidas')
            return response.data
        } catch (error) {
            tools().toast('No se pueden cargar las sesiones', 'danger', 5000)
            return [] as Sesion[]
        }
    }

    static async getEventos(): Promise<Sesion[]> {
        try {
            console.log('Solititando eventos')
            const response = await axios.get('galidancia/eventos')
            return response.data
        } catch (error) {
            tools().toast('No se pueden cargar los eventos', 'danger', 5000)
            return [] as Sesion[]
        }
    }

    static async related(sesion_id: number): Promise<Sesion[]> {
        try {
            const response = await axios.get('sesiones/related/' + sesion_id)
            return response.data
        } catch (error) {
            tools().toast('No se pueden cargar las sesiones relacionadas', 'danger', 5000)
            return [] as Sesion[]
        }
    }

    /**
     * establece los ayudantes maximos que puede tener una sesion
     * 
    */
    static async setMaxAyudantes(sesion_id: number, max_hombres: number, max_mujeres) {
        try {
            const response = await axios.patch('sesion/setAyudantesMaximos', { sesion_id: sesion_id, maxChicos: max_hombres, maxChicas: max_mujeres })
            tools().toast('Se han guardado los ayudantes máximos correctamente', 'success', 2100)

            //guardamos en el store los datos actualizados
            useSesionStore().updateMaxAyudantes(sesion_id, Number(response.data.maxChicos), Number(response.data.maxChicas))
        } catch (error) {
            tools().toast('No se pueden cargar las sesiones relacionadas', 'danger', 5000)
        }
    }

    static async cobrar(cart: object[], user_id: number, amount: number, sesion_id: number, motivo_modificacion?: string): Promise<ResultadoCobro> {
        try {
            const response = await axios.post('galidancia/cobrarActividad', {
                cart: cart,
                user_id: user_id,
                amount: amount,
                sesion_id: sesion_id,
                motivo_modificacion: motivo_modificacion
            }, { skipErrorToast: true })

            const notificacion: NotificacionCobro | null = response.data?.notificacion ?? null
            const cartId = response.data?.cart_id != null ? Number(response.data.cart_id) : null

            return { ok: true, cart_id: Number.isFinite(cartId) ? cartId : null, notificacion }
        } catch (error: any) {
            const message = error?.response?.data?.message || 'No se ha podido cobrar'
            tools().toast(message, 'danger', 6000)
            return { ok: false, cart_id: null, notificacion: null, message }
        }
    }

    static async confirmarJustificante(cartId: number, confirmacion: ConfirmacionJustificante): Promise<boolean> {
        try {
            await axios.post(`galidancia/cart/${cartId}/confirmar-justificante`, { confirmacion }, { skipErrorToast: true })
            return true
        } catch (error: any) {
            tools().toast(error?.response?.data?.message || 'No se pudo guardar la confirmación', 'warning', 4000)
            return false
        }
    }

    static async reenviarJustificante(
        cartId: number,
        canal: CanalJustificante,
        extra?: { email?: string; telefono?: string }
    ): Promise<NotificacionCobro | null> {
        try {
            const response = await axios.post(
                `galidancia/cart/${cartId}/reenviar-justificante`,
                {
                    canal,
                    ...(extra?.email ? { email: extra.email } : {}),
                    ...(extra?.telefono ? { telefono: extra.telefono } : {}),
                },
                { skipErrorToast: true }
            )
            return response.data?.notificacion ?? null
        } catch (error: any) {
            tools().toast(error?.response?.data?.message || 'No se pudo reenviar el justificante', 'danger', 5000)
            return null
        }
    }

    static async crearIncidenciaJustificante(
        cartId: number,
        extra?: { sesion_id?: number; importe?: number | string }
    ): Promise<number | null> {
        try {
            const response = await axios.post(
                `galidancia/cart/${cartId}/incidencia-justificante`,
                {
                    ...(extra?.sesion_id ? { sesion_id: extra.sesion_id } : {}),
                    ...(extra?.importe != null ? { importe: extra.importe } : {}),
                },
                { skipErrorToast: true }
            )
            return response.data?.incidencia_id != null ? Number(response.data.incidencia_id) : null
        } catch (error: any) {
            tools().toast(error?.response?.data?.message || 'No se pudo crear la incidencia', 'danger', 5000)
            return null
        }
    }


    static async videos(sesion_id: number): Promise<Video[]> {
        try {
            const response = await axios.get('sesion/videos/' + sesion_id)
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar los videos de la sesión #' + sesion_id, 'danger', 5000)
            tools().setLog('No se pudo cargar los videos de la sesión #' + sesion_id, 'error')
            return [] as Video[]
        }
    }

    static async fotogramas(sesion_id: number): Promise<Fotograma[]> {
        try {
            const response = await axios.get('sesion/fotogramas/' + sesion_id)
            return response.data
        } catch (error: any) {
            if (error?.response?.status === 403) {
                return [] as Fotograma[]
            }
            tools().toast('No se pudo cargar los fotogramas de la sesión #' + sesion_id, 'danger', 5000)
            tools().setLog('No se pudo cargar los fotogramas de la sesión #' + sesion_id, 'error')
            return [] as Fotograma[]
        }
    }


    //confirma si un alumno asistio o no a clase
    static async confirmarAsistencia(myAsistencia: Asistencia, status: 0 | 1 | 2): Promise<Asistencia> {
        try {
            const tmp = {
                asistio: status,
            } as Asistencia
            const response = await axios.put('galidancia/asistencias/' + myAsistencia.id, tmp)
            tools().toast('Asistencia confirmada', 'success', 2100)
            return response.data
        } catch (error) {
            tools().toast('No se pudo confirmar la asistencia', 'danger', 5000)
            tools().setLog('No se pudo confirmar la asistencia', 'error')
            return {} as Asistencia
        }
    }

    static async toggleAyudante(asistencia_id: number): Promise<Asistencia> {
        try {
            const response = await axios.patch('asistencia/toggleAyudante/' + asistencia_id)
            tools().toast('Se puso como ayudante correctamente', 'success', 2100)
            return response.data
        } catch (error) {
            tools().toast('No se pudo poner como ayudante', 'danger', 5000)
            return {} as Asistencia
        }
    }

    static async toggleRecuperacion(asistencia_id: number): Promise<Asistencia> {
        try {
            const response = await axios.patch('asistencia/toggleRecuperacion/' + asistencia_id)
            tools().toast('Se puso como recuperación correctamente', 'success', 2100)
            return response.data
        } catch (error) {
            tools().toast('No se pudo poner como recuperación', 'danger', 5000)
            return {} as Asistencia
        }
    }

    static async togglePrueba(asistencia_id: number): Promise<Asistencia> {
        try {
            const response = await axios.patch('asistencia/togglePrueba/' + asistencia_id)
            tools().toast('Se puso como prueba correctamente', 'success', 2100)
            return response.data
        } catch (error) {
            tools().toast('No se pudo poner como prueba', 'danger', 5000)
            return {} as Asistencia
        }
    }

    static async toggleSexo(asistencia_id: number): Promise<Asistencia> {
        try {
            const response = await axios.patch('asistencia/toggleSexo/' + asistencia_id)
            tools().toast('Se cambio el sexo correctamente', 'success', 2100)
            return response.data
        } catch (error) {
            tools().toast('No se pudo cambiar el sexo', 'danger', 5000)
            return {} as Asistencia
        }
    }

    static async getTracking(sesion_id: number): Promise<SesionLog[]> {
        try {
            const response = await axios.get('sesion/tracking/' + sesion_id)
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar el log de la sesión #' + sesion_id, 'danger', 5000)
            return {} as SesionLog[]
        }
    }

    static async setWhatsappSent(sesion_id: number, codigo: string, tlf: string, text: string, user_id: number) {
        const response = await axios.patch('sesion/setWhatsappSent/' + sesion_id, { codigo: codigo, tlf: tlf, text: text, user_id: user_id })
    }

    static async generarMensajeBienvenida(sesion_id: number, asistencia_id: number): Promise<{ mensaje: string, telefono: string } | null> {
        try {
            const response = await axios.post('sesion/generarMensajeBienvenida', {
                sesion_id: sesion_id,
                asistencia_id: asistencia_id
            })
            return response.data
        } catch (error) {
            tools().toast('No se pudo generar el mensaje de bienvenida', 'danger', 5000)
            return null
        }
    }

    /** Enlace corto (back → az7.es) o largo a app /clases/{clase_id}; fallback local si falla la petición. */
    static async getShortLinkAppClase(sesion_id: number, clase_id?: number): Promise<string> {
        try {
            const response = await axios.get('sesion/' + sesion_id + '/shortLinkAppClase')
            const u = response.data?.url
            if (typeof u === 'string' && u !== '') return u
        } catch {
            /* sin toast: el mensaje puede seguir con URL larga */
        }
        const base = (process.env.VUE_APP_GALIDANCIA_APP_URL || 'https://app.galidancia.com').replace(/\/$/, '')
        if (clase_id != null && clase_id > 0) return `${base}/clases/${clase_id}`
        return base
    }

    /** Pagadores de la actividad sin fila en ninguna sesión (alerta en icono económico). */
    static async getAlertaPagosSinAsistencia(sesion_id: number): Promise<{
        alerta: boolean
        aplica: boolean
        sin_anotar_count: number
        sin_anotar: { id: number; nombre: string }[]
        total_pagadores?: number
        total_anotados_actividad?: number
    } | null> {
        try {
            const response = await axios.get('sesion/' + sesion_id + '/alertaPagosSinAsistencia')
            return response.data
        } catch {
            return null
        }
    }

    /** Enlace de compra de entradas en la tienda (app.galidancia.com/tabs/tienda/producto/...). */
    static async getShortLinkTiendaEntradas(sesion_id: number): Promise<{ url: string; producto_id: number; producto_nombre: string } | null> {
        try {
            const response = await axios.get('sesion/' + sesion_id + '/shortLinkTiendaEntradas', {
                skipErrorToast: true,
            } as any)
            const url = response.data?.url
            if (typeof url !== 'string' || url === '') return null
            return {
                url,
                producto_id: Number(response.data?.producto_id ?? 0),
                producto_nombre: String(response.data?.producto_nombre ?? ''),
            }
        } catch {
            return null
        }
    }


    static async updateProfesor(sesion_id: number, profesor_id: number): Promise<boolean> {
        try {
            const response = await axios.patch('/sesiones/' + sesion_id + '/updateProfesor/' + profesor_id)
            useSesionStore().updateProfesor(sesion_id, profesor_id)
            tools().toast('Se actualizó el profesor correctamente', 'success', 2100)
            return true
        } catch (error) {
            console.log('No se pudo cambiar el profesor')
            console.log(error)
            tools().toast('No se pudo cambiar el profesor', 'danger', 5000)
            return false
        }
    }


    static async updateCoordinador(sesion_id: number, coordinador_id: number): Promise<boolean> {
        try {
            const response = await axios.patch('/sesiones/' + sesion_id + '/updateCoordinador/' + coordinador_id)
            tools().toast('Se actualizó el coordinador correctamente', 'success', 2100)
            return true
        } catch (error) {
            tools().toast('No se pudo cambiar el coordinador', 'danger', 5000)
            return false
        }
    }

    static async getAsistentes(sesion_id: number): Promise<Asistencia[]> {
        try {
            const response = await axios.get('sesion/asistentes/' + sesion_id)
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar los asistentes de la sesión', 'danger', 5000)
            return [] as Asistencia[]
        }
    }

    static async getNoSeAnotan(sesion_id: number): Promise<[]> {
        try {
            const response = await axios.get('sesion/noSeAnotan/' + sesion_id)
            return response.data
        } catch (error) {
            console.log(error)
            tools().toast('No se pudo cargar lista de alumnos que no se anotan', 'danger', 5000)
            return []
        }
    }

    static async ayudantesHabituales(sesion_id: number): Promise<Asistencia[]> {
        try {
            const response = await axios.get('sesion/' + sesion_id + '/ayudantesHabituales')
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar los ayudantes habituales', 'danger', 5000)
            return [] as Asistencia[]
        }
    }

    static async ayudantesNoHabituales(sesion_id: number, page = 0, sexo, nombre): Promise<Asistencia[]> {
        try {
            const response = await axios.get('sesion/' + sesion_id + '/' + page + '/ayudantesNoHabituales', { params: { sexo: sexo, nombre: nombre } })
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar los ayudantes no habituales', 'danger', 5000)
            return [] as Asistencia[]
        }
    }

    static async create(
        mySesion: Sesion,
        cobro?: { importe_cobrar?: number; excedente_asiento_genericos?: number }
    ): Promise<AlquilerCreateResponse | false> {
        try {
            const response = await axios.post('galidancia/sesiones', {
                ...mySesion,
                pagar_con_tpv: true,
                ...(cobro?.importe_cobrar != null ? { importe_cobrar: cobro.importe_cobrar } : {}),
                ...(cobro?.excedente_asiento_genericos != null
                    ? { excedente_asiento_genericos: cobro.excedente_asiento_genericos }
                    : {}),
            })
            const data = response.data as AlquilerCreateResponse
            if (data?.payment_url || data?.pendiente_tpv) {
                return data
            }
            tools().toast('La sesión se ha registrado correctamente', 'success', 5000)
            return data
        } catch (error: any) {
            const message = error.response?.data?.message || 'No se pudo registrar el alquiler'
            tools().toast(message, 'danger', 5000)
            return false
        }
    }

    static async crearSerieAlquiler(payload: {
        sesiones: Array<{
            fecha: string
            aula_id: number
            hora_inicio: string
            duracion: number
            asistentes: number
            tipo_actividad: string
            tipo_actividad_otro?: string | null
        }>
        importe_cobrar?: number
        excedente_asiento_genericos?: number
    }): Promise<(AlquilerCreateResponse & { serie_id?: number; num_sesiones?: number }) | null> {
        try {
            const response = await axios.post('galidancia/alquileres/serie', {
                ...payload,
                pagar_con_tpv: true,
            })
            const data = response.data
            if (data?.payment_url || data?.pendiente_tpv) {
                return data
            }
            tools().toast(
                `Alquiler recurrente registrado (${data.num_sesiones} sesiones)`,
                'success',
                5000
            )
            return data
        } catch (error: any) {
            const message = error.response?.data?.message || 'No se pudo registrar el alquiler recurrente'
            tools().toast(message, 'danger', 6000)
            return null
        }
    }

    static async update(mySesion: Sesion): Promise<Sesion | boolean> {
        try {
            const response = await axios.put('galidancia/sesion', mySesion)
            tools().toast('La sesión se ha actualizado correctamente', 'success', 5000)
            return response.data
        } catch (error) {
            return false
        }
    }

    static async delete(sesion_id: number): Promise<boolean> {
        try {
            await axios.delete('galidancia/sesion/' + sesion_id, {
                data: { devolver_a_asiento: 'genericos_pagan_no_vuelven' },
            })
            return true
        } catch (error: any) {
            const message = error.response?.data?.message || 'No se pudo cancelar el alquiler'
            tools().toast(message, 'danger', 5000)
            return false
        }
    }

    static async avisarAlumnosVideoDisponible(sesion_id: number): Promise<boolean> {
        try {
            const response = await axios.post('sesion/sendWhatsappVideoDisponible/' + sesion_id)
            tools().toast('Se ha avisado a los alumnos que el video está disponible', 'success', 5000)
            return response.data
        } catch (error) {
            tools().toast('No se pudo avisar a los alumnos', 'danger', 5000)
            return false
        }
    }

    static async updateAula(sesionId: number, aulaId: number): Promise<boolean> {
        try {
            const response = await axios.put(`galidancia/sesion/${sesionId}/updateAula`, { aula_id: aulaId })
            tools().toast("Aula actualizada", "success", 5000);
            return true
        } catch (error) {
            tools().toast("No se pudo actualizar el aula de la sesión", "danger", 5000);
            return false
        }
    }

    static async getContajes(sesion_id: number): Promise<ContajeSesion[]> {
        try {
            const response = await axios.get('contajeSesion/' + sesion_id)
            return response.data
        } catch (error) {
            tools().toast('No se pudo cargar los contajes de la sesion', 'danger', 5000)
            return [] as ContajeSesion[]
        }
    }

    static async getRandomSesionParaContaje(): Promise<RandomContajePayload | null> {
        try {
            const response = await axios.get('sesion/randomSesionParaContaje')
            return response.data
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return null
            }
            tools().toast('No se pudo cargar una sesión para contar', 'danger', 5000)
            return null
        }
    }

    static async setContaje(sesion_id: number, total: number): Promise<any> {
        try {
            const response = await axios.post('contajeSesion', { sesion_id: sesion_id, total: total })
            return response.data
        } catch (error) {
            tools().toast('No se pudo registrar el contaje', 'danger', 5000)
            return null
        }
    }

    static async updateAnulada(sesion_id: number, anulada: boolean, motivo_anulacion?: string, tipo_anulacion?: string): Promise<boolean> {
        try {
            const data: any = {
                sesion_id: sesion_id,
                anulada: anulada
            };

            // Si se está anulando, incluir los campos obligatorios
            if (anulada) {
                if (!motivo_anulacion || motivo_anulacion.trim().length < 10) {
                    tools().toast('El motivo de anulación debe tener al menos 10 caracteres', 'danger', 5000);
                    return false;
                }
                data.motivo_anulacion = motivo_anulacion;
                data.tipo_anulacion = tipo_anulacion || 'puntual';
            }

            const response = await axios.patch('/sesion/updateAnulada', data);
            tools().toast(anulada ? 'Sesión anulada correctamente' : 'Sesión reactivada correctamente', 'success', 5000);
            return true;
        } catch (error: any) {
            if (error.response?.data?.errors) {
                const errores = Object.values(error.response.data.errors).flat();
                tools().toast(errores.join('. '), 'danger', 8000);
            } else {
                tools().toast('No se pudo actualizar la sesión', 'danger', 5000);
            }
            return false;
        }
    }

    static async addMinutesToRental(sesion_id: number, minutes = 15): Promise<any> {
        const postAsiento = () =>
            axios.post('/sesion/addMinutesToRental', {
                sesion_id,
                minutes,
                cobrar_de_asiento: 'genericos_pagan_no_vuelven',
            })
        const postTpv = () =>
            axios.post('/sesion/addMinutesToRental', {
                sesion_id,
                minutes,
                pagar_con_tpv: true,
            })

        const asTpv = (data: any) => data?.payment_url || data?.pendiente_tpv ? data : null

        try {
            const response = await postAsiento()
            const tpv = asTpv(response.data)
            if (tpv) return tpv
            tools().toast(`Se añadieron ${minutes} minutos al alquiler`, 'success', 5000)
            return response.data
        } catch (error: any) {
            const data = error.response?.data
            const tpvEnError = asTpv(data)
            if (tpvEnError) return tpvEnError

            const pideTarjeta = error.response?.status === 402
                || data?.error === 'insufficient_asiento'
                || data?.error === 'insufficient_balance'

            if (pideTarjeta) {
                try {
                    const retry = await postTpv()
                    const tpv = asTpv(retry.data)
                    if (tpv) return tpv
                    tools().toast(`Se añadieron ${minutes} minutos al alquiler`, 'success', 5000)
                    return retry.data
                } catch (retryError: any) {
                    const retryData = retryError.response?.data
                    const tpv = asTpv(retryData)
                    if (tpv) return tpv
                    tools().toast(
                        retryData?.message
                        || data?.message
                        || 'Sin saldo en Genéricos. Completa el pago con tarjeta.',
                        'warning',
                        6000
                    )
                    return { success: false, error: 'payment_required', data: retryData || data }
                }
            }

            tools().toast(data?.message || 'No se pudieron añadir los minutos', 'danger', 5000)
            return { success: false, error: 'general_error', data }
        }
    }

    static async deleteSesion(sesion_id: number): Promise<boolean> {
        try {
            const response = await axios.delete('/sesion/' + sesion_id)
            tools().toast('Sesión borrada correctamente', 'success', 5000)
            return true
        } catch (error) {
            tools().toast('No se pudo borrar la sesión', 'danger', 5000)
            return false
        }
    }

    static async getPoliticaCancelacion(sesionId: number): Promise<any> {
        try {
            const response = await axios.get(`alquiler/politica-cancelacion?sesion_id=${sesionId}`)
            return response.data
        } catch (error) {
            console.error('Error al obtener política de cancelación:', error)
            return null
        }
    }

    static async updateStartAt(sesion_id: number, start_at: string, duration?: number): Promise<Sesion> {
        try {
            const payload: any = { start_at: start_at }
            if (typeof duration === 'number' && duration > 0) {
                payload.duration = duration
            }
            const response = await axios.patch('/sesion/update_start_at/' + sesion_id, payload)
            tools().toast('Fecha, hora y duración actualizadas correctamente', 'success', 5000)
            return response.data
        } catch (error: any) {
            if (error.response?.data?.message) {
                tools().toast(error.response.data.message, 'danger', 5000)
            } else {
                tools().toast('No se pudo actualizar la fecha y hora de la sesión', 'danger', 5000)
            }
            return {} as Sesion
        }
    }

    static async updateDuration(sesion_id: number, duration: number): Promise<Sesion> {
        try {
            const response = await axios.patch('/sesion/update_duration/' + sesion_id, { duration: duration })
            tools().toast('Duración actualizada correctamente', 'success', 5000)
            return response.data
        } catch (error: any) {
            if (error.response?.data?.message) {
                tools().toast(error.response.data.message, 'danger', 5000)
            } else {
                tools().toast('No se pudo actualizar la duración de la sesión', 'danger', 5000)
            }
            return {} as Sesion
        }
    }
}
